/**
 * Payment Provider Base Class
 * Abstract base class for all payment providers
 */

export class PaymentProvider {
    constructor(config, system) {
        this.config = config;
        this.system = system;
        this.providerType = 'base';
    }

    /**
     * Process an order with this payment provider
     * @param {Object} order - Order data
     * @returns {Promise<Object>} Processing result
     */
    async processOrder(order) {
        throw new Error('processOrder must be implemented by payment provider');
    }

    /**
     * Confirm an order after payment completion
     * @param {string} orderId - Order ID to confirm
     * @returns {Promise<Object>} Confirmation result
     */
    async confirmOrder(orderId) {
        throw new Error('confirmOrder must be implemented by payment provider');
    }

    /**
     * Render checkout UI for this provider
     * @param {Object} data - Checkout data from API
     */
    renderCheckout(data) {
        throw new Error('renderCheckout must be implemented by payment provider');
    }

    /**
     * Resolve a site URL (relative or absolute) to an absolute URL on the
     * CURRENT origin. URLs sent to payment providers must be absolute, and
     * building them from window.location.origin makes the payment return land
     * on the same origin the customer actually shopped on - hardening against
     * merchant.WebUrl origin mismatches (www vs apex, http vs https).
     * An already-absolute URL passes through unchanged.
     * @param {string} url - Configured URL (relative path or absolute)
     * @param {string} fallbackPath - Path used when url is missing
     * @returns {string} Absolute URL
     */
    static absoluteUrl(url, fallbackPath) {
        return new URL(url || fallbackPath, window.location.origin).href;
    }

    /**
     * Execute scripts in a container (common functionality)
     * @param {HTMLElement} container - Container with scripts to execute
     */
    executeScripts(container) {
        const scriptTags = container.getElementsByTagName('script');
        for (let i = 0; i < scriptTags.length; i++) {
            const parentNode = scriptTags[i].parentNode;
            const newScriptTag = document.createElement('script');
            newScriptTag.type = 'text/javascript';
            newScriptTag.text = scriptTags[i].text;
            parentNode.removeChild(scriptTags[i]);
            parentNode.appendChild(newScriptTag);
        }
    }

    /**
     * Show error message in checkout container
     * @param {string} message - Error message to display
     * @param {Error} error - Optional error object for debugging
     */
    showError(message, error = null) {
        const containers = [
            document.getElementById('klarna'),
            document.getElementById('bambora-checkout'),
            document.getElementById('stripe-checkout'),
            document.getElementById('none-checkout'),
            document.getElementById('checkout-container')
        ].filter(el => el !== null);
        
        const container = containers[0];
        if (container) {
            const isTestMode = this.providerType === 'none';
            container.innerHTML = `
                <div class="checkout-error" style="padding: 20px; background: ${isTestMode ? '#fff3cd' : '#f8d7da'}; color: ${isTestMode ? '#856404' : '#721c24'}; border: 1px solid ${isTestMode ? '#ffc107' : '#f5c6cb'}; border-radius: 5px; margin: 20px 0;">
                    <h4>${isTestMode ? '🧪 Test Mode Error' : 'Checkout-fel'}</h4>
                    <p>${this.system.escapeHtml(message)}</p>
                    ${error && this.config.debug ? `<pre style="font-size: 12px; margin-top: 10px; opacity: 0.7;">${error.message}</pre>` : ''}
                    <div style="margin-top: 15px;">
                        <button onclick="location.reload()" class="btn btn-primary-filled" style="margin-right: 10px;">Försök igen</button>
                        <button onclick="history.back()" class="btn btn-secondary" style="background: #6c757d; color: white; padding: 8px 16px; border: none; border-radius: 4px;">Tillbaka</button>
                    </div>
                </div>
            `;
        }
    }
}
